import React, { useEffect, useState, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Button, CircularProgress } from '@mui/material'
import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { getTokenData, setTokenData } from 'util/countdown-store' // Updated IndexedDB functions
import CodeInput from 'components/CodeInput'
import { generateTokenService, validateTokenService } from 'services/token'
import { useTurnstile } from '../../hooks/useTurnstile'
import { Turnstile } from '@marsidev/react-turnstile'

import * as yup from 'yup'
import * as styles from './UserVerificationForm.module.scss'
import { Captcha } from 'components/Captcha'

export const UserVerificationForm = ({
  email = 'a@gmail.com',
  firstName,
  lastName,
  goToInfo,
  onConfirm,
  verificationCodeExpiration = 900, // Test with 30 seconds
  manualResendCooldown = 180, // 3 minutes
  hasTurnstile = false,
  formClassName,
  titleClassName = styles.header,
}) => {
  const recaptchaRef = React.useRef(null)
  const [isGettingTime, setGettingTime] = useState(true)
  const [isInProgress, setIsInProgress] = useState(false)
  const [secondsToResend, setSecondsToResend] = useState(
    verificationCodeExpiration,
  )
  const { turnstileRef, getTurnstileToken, setTurnstileToken } = useTurnstile()
  const [manualCooldown, setManualCooldown] = useState(manualResendCooldown)
  const intervalRef = useRef(null)
  const [turnstileTokenReady, setTurnstileTokenReady] = useState(false)
  const [codeInputValid, setCodeInputValid] = useState(false)

  const yupResolver = yup.object().shape({
    ...(!hasTurnstile && {
      captchaResponseToken: yup
        .string()
        .required('Captcha is required')
        .typeError('Captcha is required'),
    }),
    codeInput: yup
      .string()
      .required('Code is required')
      .typeError('Code is required'),
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
  } = useForm({
    resolver: useYupValidationResolver(yupResolver),
  })

  const codeInput = register('codeInput', { required: true })

  const handleCodeInputChange = input => {
    codeInput.onChange({
      target: {
        value: input,
        name: 'codeInput',
      },
    })
    setCodeInputValid(input && input.length === 6)
  }

  const memoizedEmail = React.useMemo(() => email, [email])

  const sendToken = async () => {
    await generateTokenService({
      firstName,
      lastName,
      tokenSource: 'RECEIPT_UPLOAD',
      email: memoizedEmail,
      type: 'EMAIL',
    })
    const now = Date.now()
    await setTokenData(memoizedEmail, {
      tokenExpiresAt: now + verificationCodeExpiration * 1000,
      canSendAnNewTokenAt: now + manualResendCooldown * 1000,
    })
  }

  useEffect(() => {
    const initializeTokenFlow = async () => {
      const tokenData = await getTokenData(memoizedEmail)
      const now = Date.now()

      if (!tokenData || tokenData.tokenExpiresAt <= now) {
        await sendToken()
      }

      setSecondsToResend(
        tokenData
          ? Math.max(0, (tokenData.tokenExpiresAt - now) / 1000)
          : verificationCodeExpiration,
      )
      setManualCooldown(
        tokenData
          ? Math.max(0, (tokenData.canSendAnNewTokenAt - now) / 1000)
          : manualResendCooldown,
      )
    }

    initializeTokenFlow()

    intervalRef.current = setInterval(async () => {
      const tokenData = await getTokenData(memoizedEmail)
      const now = Date.now()

      if (tokenData) {
        const timeToResend = Math.max(
          0,
          (tokenData.tokenExpiresAt - now) / 1000,
        )
        const timeToManualResend = Math.max(
          0,
          (tokenData.canSendAnNewTokenAt - now) / 1000,
        )

        setSecondsToResend(timeToResend)
        setManualCooldown(timeToManualResend)
      }
    }, 1000)

    return () => clearInterval(intervalRef.current)
  }, [memoizedEmail])

  useEffect(() => {
    setTimeout(() => setGettingTime(false), 1500)
  }, [])

  const onSubmit = async (data, e) => {
    if (!data.captchaResponseToken && !hasTurnstile) {
      setError('captchaResponseToken', {
        type: 'manual',
        message: 'Please complete the captcha',
      })
      return
    }
    e.preventDefault()
    setIsInProgress(true)

    const confirmationData = {
      tokenSource: 'RECEIPT_UPLOAD',
      email: memoizedEmail,
      token: data.codeInput,
      type: 'EMAIL',
    }

    const isValid = await validateTokenService(confirmationData)

    if (isValid && hasTurnstile) {
      const turnstileToken = await getTurnstileToken()
      if (!turnstileToken) {
        setError('turnstile', {
          type: 'manual',
          message: 'Failed to verify you are human. Please try again.',
        })
        return
      }

      onConfirm({
        confirmed: true,
        values: {
          confirmed: true,
          turnstileToken,
        },
      })
    } else if (isValid) {
      onConfirm({
        confirmed: true,
        values: {
          confirmed: true,
          recaptchaResponseToken: data.captchaResponseToken,
        },
      })
    } else {
      setError('codeInput', { type: 'manual', message: 'Invalid code' })
    }
    if (!hasTurnstile) {
      recaptchaRef.current.reset()
    }

    setIsInProgress(false)
  }

  return (
    <FormProvider>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={formClassName ? formClassName : styles.formWrapper}
      >
        <div className={styles.wrapper}>
          <span className={titleClassName}>Let's get you verified!</span>
          <div className={styles.content}>
            <span>
              A verification code has been sent to your email. Please check your
              inbox and enter the code below.
            </span>
            <div className={styles.emailInfo}>
              <span>Email Linked to Your Account</span>
              <span className={styles.email}>{`*${email}`}</span>
            </div>
            <div className={styles.codeArea}>
              <label htmlFor="codeInput">Enter the 6 digit code</label>
              <CodeInput
                width={32}
                height={52}
                ref={codeInput.ref}
                onChange={handleCodeInputChange}
              />
              {errors.codeInput && (
                <span className="error-message">
                  {errors.codeInput.message}
                </span>
              )}
              <p
                style={{
                  marginBottom: '0px',
                }}
              >
                {isGettingTime ? (
                  <CircularProgress size={10} />
                ) : secondsToResend > 0 ? (
                  `This code expires in
                ${new Date(secondsToResend * 1000).toISOString().substr(14, 5)} 
                ${secondsToResend > 60 ? 'minutes.' : 'seconds.'}`
                ) : (
                  'Code expired. Please request a new one.'
                )}
              </p>
              {!hasTurnstile ? (
                <Captcha
                  ref={recaptchaRef}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                  onChange={e => setValue('captchaResponseToken', e)}
                />
              ) : (
                <Turnstile
                  ref={turnstileRef}
                  options={{
                    refreshExpired: 'manual',
                  }}
                  siteKey={process.env.VISIBLE_TURNSTILE_SITE_KEY}
                  onSuccess={token => {
                    setTurnstileToken(token)
                    setTurnstileTokenReady(true)
                  }}
                  onError={error => {
                    console.error('Turnstile error:', error)
                    setTurnstileToken(null)
                    setTurnstileTokenReady(false)
                  }}
                  onExpire={() => {
                    console.log('Turnstile token expired')
                    setTurnstileToken(null)
                    setTurnstileTokenReady(false)
                    turnstileRef.current?.reset()
                  }}
                />
              )}
              {errors.captchaResponseToken && (
                <span className="error-message">
                  {errors.captchaResponseToken.message}
                </span>
              )}
              <Button
                disabled={
                  isInProgress ||
                  (hasTurnstile && !turnstileTokenReady) ||
                  !codeInputValid
                }
                className={styles.button}
                variant="contained"
                type="submit"
              >
                {isInProgress ? <CircularProgress size={18} /> : 'Confirm Code'}
              </Button>
            </div>
            {isGettingTime ? (
              <CircularProgress style={{ alignSelf: 'center' }} size={10} />
            ) : !isGettingTime && manualCooldown > 0 ? (
              <p>
                Haven't received a code in your inbox? You can request a new one
                in {new Date(manualCooldown * 1000).toISOString().substr(14, 5)}{' '}
                {manualCooldown > 60 ? 'minutes.' : 'seconds.'}
              </p>
            ) : (
              <button
                disabled={isInProgress || manualCooldown > 0}
                onClick={goToInfo}
                className={styles.resendLink}
              >
                I didn't receive an email
              </button>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
